// src/pages/Users/Inscription/AdherentsMiniTable.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "./../../../components/Button/Button";
import { useUser } from "./../../Home/UserContext";
import { getAdherentsByUser } from "./../../../services/adherentServices";
import { getGroupName } from "./../../../services/groupServices";
import { getSettings } from "./../../../services/settingsServices";
import RenewalModal from "./../InscriptionAdherent/RenewalModal";
import { toast } from "react-toastify";
import { FaExternalLinkAlt, FaRedo } from "react-icons/fa";

/* Helpers statut (copiés/simplifiés de ta page) */
function getStatusClass(statut) {
  switch (statut) {
    case "Licencié":
      return "bg-green-300 text-slate-800";
    case "Licencié Hors Club":
      return "bg-green-200 text-slate-800";
    case "Inscrit":
      return "bg-yellow-300 text-slate-900";
    case "Cours d'essai":
      return "bg-red-300 text-slate-900";
    case "Licencié - Arrêt":
    case "Ancien Licencié et Arrêt":
      return "bg-slate-300 text-slate-800";
    case "Ancien Licencié":
      return "bg-slate-900 text-white";
    case "Non Licencié - Arrêt":
      return "bg-slate-500 text-white";
    default:
      return "bg-slate-200 text-slate-800";
  }
}
const seasonTagClass = (saison, current) =>
  saison && current && saison === current
    ? "bg-emerald-200 text-emerald-900 ring-1 ring-emerald-300"
    : "bg-slate-200 text-slate-800 ring-1 ring-slate-300";

const defaultAvatar = (genre) =>
  genre === "Féminin" ? "/img/judokate_avatar.png" : "/img/judoka_avatar.png";

/**
 * Petit tableau récap des adhérents de l'utilisateur
 * Props:
 *  - className? : string (optionnel)
 *  - limit? : number (nb d’adhérents affichés, défaut 5)
 */
export default function AdherentsMiniTable({ className = "", limit = 5 }) {
  const { user } = useUser();
  const userId = user?.userId;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [adherents, setAdherents] = useState([]);
  const [groupNames, setGroupNames] = useState({});
  const [activeSeason, setActiveSeason] = useState("");

  // Modal Renouvellement
  const [showRenew, setShowRenew] = useState(false);
  const [renewAdherent, setRenewAdherent] = useState(null);

  useEffect(() => {
    (async () => {
      if (!userId) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);

        const settings = await getSettings();
        setActiveSeason(settings?.[0]?.saison_active || "");

        const data = await getAdherentsByUser(userId);
        const list = Array.isArray(data) ? data : [];
        setAdherents(list);

        // Récup noms de groupes par adhérent (tableau de noms)
        const names = {};
        await Promise.all(
          list.map(async (a) => {
            try {
              const g = await getGroupName(a.id);
              names[a.id] = g || [];
            } catch {
              names[a.id] = [];
            }
          })
        );
        setGroupNames(names);
      } catch (e) {
        console.error(e);
        toast.error("Erreur lors du chargement de vos adhérents.");
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  const rows = useMemo(() => adherents.slice(0, limit), [adherents, limit]);

  if (!userId) return null; // rien si non connecté
  if (loading)
    return (
      <div
        className={
          "mb-4 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 bg-white/80 dark:bg-slate-900/60 " +
          className
        }
      >
        <div className="animate-pulse h-5 w-2/3 bg-slate-200 dark:bg-slate-700 rounded mb-3" />
        <div className="animate-pulse h-10 w-full bg-slate-100 dark:bg-slate-800 rounded" />
      </div>
    );

  const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    // déjà absolu, data URL ou blob
    if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) return s;
    // chemins d'assets front (ex: /img/...): on laisse tel quel
    if (!s.startsWith("/uploads")) return s;
    // fichiers uploadés côté API
    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };

  if (adherents.length === 0) return null; // ne rien afficher si aucun adhérent

  return (
    <div
      className={
        "mb-5 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/60 overflow-hidden " +
        className
      }
    >
      {/* Bandeau d’info */}
      <div className="px-4 sm:px-5 py-3 bg-slate-50/80 dark:bg-slate-900/50">
        <p className="text-sm text-slate-700 dark:text-slate-200">
          <span className="font-semibold">
            Vous gérez déjà {adherents.length} adhérent(s).
          </span>{" "}
          Avant de créer une nouvelle fiche, vérifiez s’il s’agit d’un{" "}
          <span className="font-semibold">renouvellement</span>.
        </p>
      </div>

      {/* Table desktop */}
      <div className="hidden md:block px-4 sm:px-5 py-4">
        <div className="overflow-x-auto rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white/90 dark:bg-slate-900/40">
          <table className="min-w-[760px] w-full text-sm">
            <thead className="bg-slate-50/90 dark:bg-slate-900/70">
              <tr className="text-left text-slate-700 dark:text-slate-200">
                <th className="px-4 py-3">Photo</th>
                <th className="px-4 py-3">Nom / Prénom</th>
                <th className="px-4 py-3">Saison</th>
                <th className="px-4 py-3">Groupes</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {rows.map((a) => {
                const canRenew =
                  activeSeason && a.saison_active !== activeSeason;
                return (
                  <tr
                    key={a.id}
                    className="bg-white/90 dark:bg-slate-900/40 hover:bg-slate-50 dark:hover:bg-slate-900/60 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <img
                        src={toAbsolute(a.photo_url) || defaultAvatar(a.genre)}
                        alt={`${a.nom || ""} ${a.prenom || ""}`}
                        className="h-10 w-10 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                      />
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">
                      {(a.nom || "").toUpperCase()} {a.prenom || ""}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${seasonTagClass(
                          a.saison_active,
                          activeSeason
                        )}`}
                        title={`Saison actuelle : ${activeSeason || "—"}`}
                      >
                        {a.saison_active || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-800 dark:text-slate-100">
                      {groupNames[a.id]?.length
                        ? groupNames[a.id].join(", ")
                        : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ${getStatusClass(
                          a.statut_inscription
                        )}`}
                      >
                        {a.statut_inscription || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-center items-center gap-2">
                        <Button
                          text="Voir le dossier"
                          icon={FaExternalLinkAlt}
                          onClick={() => navigate("/dossier-d-inscription")}
                          className="!rounded-xl !px-3 !py-1.5 !text-xs gap-2"
                        />
                        {canRenew && (
                          <Button
                            text="Renouveler"
                            icon={FaRedo}
                            onClick={() => {
                              setRenewAdherent(a);
                              setShowRenew(true);
                            }}
                            className="!rounded-xl !px-3 !py-1.5 !text-xs gap-2"
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Lien voir tout si coupure */}
        {adherents.length > limit && (
          <div className="mt-3 text-right">
            <Button
              text={`Voir tous (${adherents.length})`}
              onClick={() => navigate("/dossier-d-inscription")}
              className="!rounded-xl !px-3 !py-1.5 !text-xs"
            />
          </div>
        )}
      </div>

      {/* Cartes mobile */}
      <div className="md:hidden px-4 sm:px-5 py-4 space-y-3">
        {rows.map((a) => {
          const canRenew = activeSeason && a.saison_active !== activeSeason;
          return (
            <div
              key={a.id}
              className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-3 bg-white/90 dark:bg-slate-900/40"
            >
              <div className="flex items-center gap-3">
                <img
                  src={toAbsolute(a.photo_url) || defaultAvatar(a.genre)}
                  alt={`${a.nom || ""} ${a.prenom || ""}`}
                  className="h-10 w-10 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                />
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 dark:text-slate-100 truncate">
                    {(a.nom || "").toUpperCase()} {a.prenom || ""}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${seasonTagClass(
                        a.saison_active,
                        activeSeason
                      )}`}
                    >
                      {a.saison_active || "—"}
                    </span>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${getStatusClass(
                        a.statut_inscription
                      )}`}
                    >
                      {a.statut_inscription || "—"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-2 text-sm text-slate-700 dark:text-slate-300">
                <span className="opacity-70">Groupes :</span>{" "}
                {groupNames[a.id]?.length ? groupNames[a.id].join(", ") : "—"}
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  text="Voir le dossier"
                  icon={FaExternalLinkAlt}
                  onClick={() => navigate("/dossier-d-inscription")}
                  className="!rounded-xl !px-3 !py-1.5 !text-xs"
                />
                {canRenew && (
                  <Button
                    text="Renouveler"
                    icon={FaRedo}
                    onClick={() => {
                      setRenewAdherent(a);
                      setShowRenew(true);
                    }}
                    className="!rounded-xl !px-3 !py-1.5 !text-xs"
                  />
                )}
              </div>
            </div>
          );
        })}

        {adherents.length > limit && (
          <div className="pt-1">
            <Button
              text={`Voir tous (${adherents.length})`}
              onClick={() => navigate("/dossier-d-inscription")}
              className="!rounded-xl !px-3 !py-1.5 !text-xs"
            />
          </div>
        )}
      </div>

      {/* Modal de renouvellement */}
      {showRenew && (
        <RenewalModal
          isOpen={showRenew}
          adherent={renewAdherent}
          nextSeason={activeSeason}
          onClose={async (saved) => {
            setShowRenew(false);
            setRenewAdherent(null);
            if (saved) {
              // rafraîchir rapidement
              try {
                setLoading(true);
                const data = await getAdherentsByUser(userId);
                setAdherents(Array.isArray(data) ? data : []);
              } catch {
                /* ignore */
              } finally {
                setLoading(false);
              }
            }
          }}
        />
      )}
    </div>
  );
}
